import { useCallback, useEffect, useState } from 'react';
import { rpc, supabaseConfigured } from '../lib/supabase';

const EMPTY = { status: 'live', products: [], total: 0 };
const clean = v => (v === '' || v === '전체' || v == null ? null : v);

// 금리 조회: Supabase search_products. compact=true면 목록용 필드만 받습니다.
export function useRates(filters = {}) {
  const key = JSON.stringify(filters);
  const [data, setData] = useState({ status: 'loading', products: [], total: 0 });
  useEffect(() => {
    const f = JSON.parse(key);
    let active = true;
    if (!supabaseConfigured) { setData({ status: 'error', products: [], total: 0, error: 'Supabase 설정이 없습니다' }); return; }
    if (Array.isArray(f.banks) && (!f.banks.length || f.banks.every(b => b === '__none__'))) { setData(EMPTY); return; }
    setData(prev => ({ ...prev, status: 'loading' }));
    rpc('search_products', {
      p_kind: clean(f.kind), p_tier: clean(f.tier), p_banks: f.banks?.length ? [...f.banks].sort() : null,
      p_q: clean(f.q), p_id: clean(f.id), p_term: Number(f.term) || 12, p_sort: f.sort === 'base' ? 'base' : 'rate',
      p_limit: Number(f.limit) || 30, p_offset: Number(f.offset) || 0, p_best_by_bank: Boolean(f.bestByBank), p_compact: Boolean(f.compact),
    })
      .then(value => { if (active) setData(value); })
      .catch(error => { if (active) setData({ status: 'error', products: [], total: 0, error: error.message }); });
    return () => { active = false; };
  }, [key]);
  return data;
}

const PAGE = 20;
const HOUR = 60 * 60 * 1000;

// 지점·ATM: 검색한 지역(area) 안에서 중심과 가까운 순으로 20개씩 불러옵니다.
// sector: 'bank'|'savings'|'cu'|'mg'|'nh'를 주면 그 금융권 지점만 (ATM은 그대로)
export function usePlaces(area, includeAtm = true, sector = null) {
  const areaKey = area ? `${area.key}|${area.bounds.map(n => n.toFixed(4)).join(',')}|${includeAtm}|${sector || ''}` : '';
  const [state, setState] = useState({ status: 'idle', items: [], total: 0, banks: 0 });
  const load = useCallback((offset, replace) => {
    if (!area) return;
    const [s, w, n, e] = area.bounds;
    setState(prev => ({ ...(replace ? { items: [], total: 0, banks: 0 } : prev), status: 'loading' }));
    rpc('places_page', { p_south: s, p_west: w, p_north: n, p_east: e, p_lat: area.center[0], p_lng: area.center[1], p_limit: PAGE, p_offset: offset, p_include_atm: includeAtm, p_sector: sector }, HOUR)
      .then(page => setState(prev => {
        const base = replace ? [] : prev.items;
        const seen = new Set(base.map(p => p.id));
        return { status: 'live', items: [...base, ...page.items.filter(p => !seen.has(p.id))], total: page.total, banks: page.banks };
      }))
      .catch(error => setState(prev => ({ ...prev, status: 'error', error: error.message })));
  }, [areaKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(0, true); }, [load]);
  const loadMore = useCallback(() => load(state.items.length, false), [load, state.items.length]);
  return { ...state, hasMore: state.items.length < state.total, loadMore };
}

const IDLE_RADAR = { status: 'idle', products: [], total: 0, onlineBest: null };

// 창구 특판 레이더: 불러온 지점의 은행 중 '영업점에서만 가입되는' 상품과,
// 같은 은행들의 비대면 최고 금리(onlineBest)를 함께 받습니다.
export function useRadar({ banks = [], kind, term, tier, enabled }) {
  const key = enabled ? JSON.stringify({ banks: [...banks].sort(), kind, term, tier: clean(tier) }) : '';
  const [data, setData] = useState(IDLE_RADAR);
  useEffect(() => {
    if (!key) { setData(IDLE_RADAR); return; }
    const f = JSON.parse(key);
    if (!f.banks.length || f.banks.every(b => b === '__none__')) { setData({ ...IDLE_RADAR, status: 'live' }); return; }
    let active = true;
    setData(prev => ({ ...prev, status: 'loading' }));
    rpc('radar_products', { p_banks: f.banks, p_kind: f.kind, p_term: Number(f.term) || 12, p_tier: f.tier, p_best_by_bank: true, p_limit: 60 })
      .then(value => { if (active) setData({ status: 'live', ...value }); })
      .catch(error => { if (active) setData({ ...IDLE_RADAR, status: 'error', error: error.message }); });
    return () => { active = false; };
  }, [key]);
  return data;
}

// 동네 금리 온도: 시·군·구 범위(bounds) 안 지점별 최고 금리. 시·군·구마다 따로 캐시됩니다.
export function areaRates(bounds, { kind, term, tier }) {
  const [s, w, n, e] = bounds.map(v => Math.round(v * 1000) / 1000);
  return rpc('area_rates', { p_south: s, p_west: w, p_north: n, p_east: e, p_kind: kind, p_term: Number(term) || 12, p_tier: clean(tier) });
}
